import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, ArrowRight, Check, Flag, Plus, Send, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Modal } from "../../components/ui/modal";
import { FilterChip } from "../../components/ui/primitives";
import { instagramPublicationTarget, useProjectAccounts } from "../../lib/accounts";
import { EDITORS, editorsOf, editorsOfProject, GROUP_OF, homeProject, ME_CLIENT, rememberProject, PEOPLE, projectPublishesAfterModeration, projectsOf, type Dept, type PersonId } from "../../data/people";
import { DueDateTime } from "../../components/DueDateTime";
import { defaultDue, dueOptions, freestOn, liveItems, PUB_FORMATS } from "../../lib/domain";
import { knownFormat, readDirectionPrefs, rememberDirectionPrefs } from "../../lib/directionPrefs";
import { notify } from "../../lib/notify";
import { at, dayName, dayOf, DAYS, hourOf } from "../../lib/time";
import { useNow } from "../../lib/useNow";
import { cn, plural, uid } from "../../lib/utils";
import { boardHides, clearBoardFilters, useStore, useTask, type NewTaskPreset } from "../../store";
import type { SubTask, Task } from "../../types";
import { BatchNewTasks } from "./BatchNewTasks";
import { AccountsPick } from "../../components/AccountsPick";
import { DayByPeople } from "./calendars/DayByPeople";

/** Смена шага: старый быстро гаснет, модалка меняет размер, новый проявляется, когда она почти на месте */
const stepFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.22, delay: 0.16, ease: [0.22, 1, 0.36, 1] as const } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};


/** Высота шага «Кому и на какой день» — постоянная, календарь внутри прокручивается */
const pickHeight = () => Math.min(780, window.innerHeight - 48);

export function NewTaskModal() {
  const nt = useStore((s) => s.newTask);
  const openNewTask = useStore((s) => s.openNewTask);
  const task = useTask(nt?.mode === "reschedule" ? nt.taskId : null);
  const [step, setStep] = useState<1 | 2>(1);
  // Высота первого шага, «замороженная» перед переходом: модалка растёт от неё, а не прыгает
  const [freeze, setFreeze] = useState<number | undefined>();
  // Несколько публикаций за раз (09.10.2026): своё окно-список вместо двух шагов.
  const [batch, setBatch] = useState<BatchOpen | null>(null);
  const openTask = useStore((s) => s.openTask);
  // Пришли из окна публикации («Открыть календарь загрузки») — туда и вернёмся.
  const close = () => {
    const back = nt?.mode === "reschedule" && nt.back ? nt.taskId : null;
    openNewTask(null);
    if (back) window.setTimeout(() => openTask(back), 220);
  };
  const open = !!nt && (nt.mode === "create" || !!task);

  useEffect(() => {
    if (nt?.mode === "create") {
      setStep(1);
      setFreeze(undefined);
    }
    setBatch(null);
  }, [nt]);
  const realStep = nt?.mode === "reschedule" ? 2 : step;

  const goStep = (s: 1 | 2) => {
    if (s === 2) {
      const h = document.querySelector<HTMLElement>("[role=dialog]")?.getBoundingClientRect().height;
      setFreeze(h ? Math.round(h) : undefined);
      requestAnimationFrame(() => setStep(2));
    } else {
      setFreeze(undefined);
      setStep(1);
    }
  };

  return (
    <Modal open={open} onClose={close} width={batch ? 1200 : realStep === 1 ? 880 : 1200} height={batch || realStep === 2 ? pickHeight() : freeze}>
      {nt && batch && <BatchNewTasks dept={batch.dept} first={batch.first} onClose={close} />}
      {nt && !batch && <Flow key={nt.mode === "create" ? "create" : nt.taskId} task={task} preset={nt.mode === "create" ? nt.preset : undefined} step={realStep} setStep={goStep} onClose={close} onBatch={setBatch} />}
    </Modal>
  );
}

type BatchOpen = { dept: Dept; first: { title: string; format: string; brief: string } };

function Flow({
  task,
  preset,
  step,
  setStep,
  onClose,
  onBatch,
}: {
  task?: Task;
  preset?: NewTaskPreset;
  step: 1 | 2;
  setStep: (s: 1 | 2) => void;
  onClose: () => void;
  onBatch: (b: BatchOpen) => void;
}) {
  const now = useNow();
  const tasks = useStore((s) => s.tasks);
  const busy = useStore((s) => s.busy);
  const createTask = useStore((s) => s.createTask);
  const reschedule = useStore((s) => s.reschedule);
  const restore = useStore((s) => s.restore);
  const cancelTask = useStore((s) => s.cancelTask);
  const isReschedule = !!task;

  // Тема — то, что ставится: название, что снять, на день или на неделю.
  const [topicTitle, setTopicTitle] = useState(task?.title ?? "");
  const [description, setDescription] = useState(task?.description ?? "");
  const [period] = useState<"day" | "week">(task?.period ?? "day");
  const [titleError, setTitleError] = useState(0);
  // Свободная тема: исполнителя нет, её возьмёт монтажёр группы или назначат позже.
  const [unassigned, setUnassigned] = useState(Boolean(task && !task.editor));

  // Объём не спрашиваем: задачи день в день; для раскладки в календаре берём 2 ч
  const estimate = task?.estimate ?? 2;
  // Задачи дают на день и делают день в день: по умолчанию — сегодня (если сроки на сегодня ещё не прошли) и тот, у кого меньше задач
  const today = dayOf(now);
  const startDay = preset?.day ?? (DAYS[0].idx === today && dueOptions(today, now).length > 0 ? today : (DAYS.find((d) => d.idx > today) ?? DAYS[0]).idx);
  // Отдел: у каждого свои монтажёры. По умолчанию — отдел задачи, монтажёра из календаря или свой
  const initialDept: Dept = task?.dept ?? (preset?.editor ? PEOPLE[preset.editor].dept : undefined) ?? PEOPLE[ME_CLIENT].dept ?? "Редакция";
  const [dept, setTaskDept] = useState<Dept>(initialDept);
  // Проект: ролик выходит в его паблики, и от него же зависит, чьи монтажёры
  // доступны — работают только группы, заведённые в проект.
  const deptProjects = projectsOf(dept);
  const [projectId, setProjectId] = useState<string | null>(
    () => task?.projectId ?? (preset?.projectId && deptProjects.some((p) => p.id === preset.projectId) ? preset.projectId : null) ?? homeProject(deptProjects, GROUP_OF[ME_CLIENT])?.id ?? null,
  );
  const deptEditors = projectId ? editorsOfProject(projectId) : editorsOf(dept);
  // Прошлый выбор в этом направлении: аккаунты, формат, автопубликация (02.11.2026).
  const [prefs] = useState(() => readDirectionPrefs(projectId));
  const [format, setFormat] = useState<string>(() => knownFormat(prefs.format));
  // Куда выходит ролик: одна публикация — один аккаунт (08.10.2026).
  const projectAccounts = useProjectAccounts(projectId);
  // Несколько аккаунтов (09.10.2026, Артур): один ролик на несколько каналов —
  // по публикации на канал. С 02.11.2026 ставятся прямо отсюда, без окна пакета.
  const [accountIds, setAccountIds] = useState<string[]>(() => prefs.accountIds ?? []);
  // В «Президенте» после «Норм» ролик всегда уходит в Instagram сам — галочку не снять (28.10.2026).
  const [autoPublishChoice, setAutoPublishInstagram] = useState<boolean | null>(() => prefs.autoPublish ?? null);
  const afterModeration = projectPublishesAfterModeration(projectId);
  const autoPublishInstagram = afterModeration || (autoPublishChoice ?? false);
  const chosen = accountIds.filter((id) => projectAccounts.some((a) => a.id === id));
  const account = projectAccounts.find((a) => a.id === chosen[0]) ?? projectAccounts[0] ?? null;
  const effectiveAccountIds = chosen.length ? chosen : account ? [account.id] : [];
  const instagramAccounts = projectAccounts.filter((candidate) => effectiveAccountIds.includes(candidate.id) && candidate.platform === "instagram");
  const hasInstagramAccount = instagramAccounts.length > 0;
  const publicationTarget = instagramPublicationTarget(instagramAccounts);
  const several = chosen.length > 1;
  const [editor, setEditor] = useState<PersonId | null>(() => {
    if (task?.editor || preset?.editor) return (task?.editor || preset?.editor)!;
    const first = task?.projectId ?? preset?.projectId ?? homeProject(projectsOf(initialDept), GROUP_OF[ME_CLIENT])?.id ?? null;
    const list = first ? editorsOfProject(first) : editorsOf(initialDept);
    return list.length ? freestOn(startDay, tasks, busy, list) : null;
  });
  const [day, setDay] = useState(() => task?.day ?? startDay);
  const [due, setDue] = useState(() => task?.due ?? defaultDue(task?.day ?? startDay, now));
  // Срок не из списка «до 12…20:00» — своя дата и время.

  const ed = editor ? PEOPLE[editor] : null;

  // Неделя темы — с понедельника той недели, куда попал выбранный день; срок —
  // пятница 20:00. День недели по номеру: 0 — понедельник этой недели.
  const mondayOf = (d: number) => d - (((d % 7) + 7) % 7);
  const workDay = period === "week" ? mondayOf(day) : day;
  const dueAt = period === "week" ? at(mondayOf(day) + 4, 20) : due;

  /** Новое направление — его прошлые аккаунты и автопубликация. */
  const applyPrefs = (id: string | null) => {
    const p = readDirectionPrefs(id);
    setAccountIds(p.accountIds ?? []);
    setAutoPublishInstagram(p.autoPublish ?? null);
  };

  const changeDept = (d: Dept) => {
    setTaskDept(d);
    const next = homeProject(projectsOf(d), GROUP_OF[ME_CLIENT])?.id ?? null;
    setProjectId(next);
    applyPrefs(next);
    const list = next ? editorsOfProject(next) : editorsOf(d);
    setEditor(list.length ? (editor && list.includes(editor) ? editor : freestOn(day, tasks, busy, list)) : null);
  };

  const changeProject = (id: string) => {
    setProjectId(id);
    applyPrefs(id);
    const list = editorsOfProject(id);
    setEditor(list.length ? (editor && list.includes(editor) ? editor : freestOn(day, tasks, busy, list)) : null);
  };

  const pick = (e: PersonId, d: number) => {
    if (deptEditors.includes(e)) setEditor(e);
    setUnassigned(false);
    setDay(d);
    const same = at(d, hourOf(due));
    setDue(same > now ? same : defaultDue(d, now));
  };

  const next = () => {
    if (topicTitle.trim().length < 3) {
      setTitleError((n) => n + 1);
      return;
    }
    setStep(2);
  };

  const submit = () => {
    if (!unassigned && (!editor || !ed)) return;
    if (due <= now) return;
    // Без проекта задачу не принять: ролику некуда выходить, и сервер откажет.
    if (!task && !projectId) return;
    if (task) {
      const snap: Task = JSON.parse(JSON.stringify(task));
      reschedule(task.id, unassigned ? "" : editor!, workDay, estimate, dueAt);
      onClose();
      notify(`Готово — ${unassigned ? "свободная публикация" : ed!.name}, ${dayName(workDay, now).toLowerCase()}`, { icon: "check", undo: () => restore(snap) });
      return;
    }
    // Публикация — ровно один ролик: заголовок и описание публикации и есть его.
    // Несколько каналов — по такой публикации на каждый (09.10.2026, Артур).
    const targets = several ? chosen : [account?.id ?? null];
    const whomId = unassigned ? "" : editor!;
    const ids = targets.map((accountId) => {
      const sub: SubTask = { id: uid(), title: topicTitle.trim(), format, brief: description.trim(), accountId, files: [] } as unknown as SubTask;
      return createTask({
        title: topicTitle.trim(), description: description.trim(), period,
        dept, projectId: projectId!,
        autoPublishInstagram,
        items: [sub], creator: ME_CLIENT, editor: whomId, day: workDay, estimate, due: dueAt,
      });
    });
    rememberProject(projectId!);
    rememberDirectionPrefs(projectId, { accountIds: chosen.length ? chosen : effectiveAccountIds, format, autoPublish: autoPublishChoice ?? undefined });
    onClose();
    // Поиск и фильтры доски не сбрасываем (02.11.2026): если они прячут новую
    // карточку — «Показать» в тосте.
    const hidden = boardHides({ title: topicTitle.trim(), projectId: projectId!, editor: whomId });
    const whom = unassigned ? "свободная" : ed!.name;
    const when = period === "week" ? `неделя с ${dayName(workDay, now).toLowerCase()}` : dayName(workDay, now).toLowerCase();
    const what = ids.length > 1 ? `${ids.length} ${plural(ids.length, ["публикация поставлена", "публикации поставлены", "публикаций поставлено"])}` : "Публикация поставлена";
    notify(`${what} · ${whom} — ${when}`, {
      icon: "send",
      undo: () => ids.forEach((id) => cancelTask(id)),
      action: hidden ? { label: "Показать", run: clearBoardFilters } : undefined,
    });
  };


  return (
    <>
      <div className="flex shrink-0 items-start gap-3 pb-1 pl-7 pr-5 pt-6">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <AnimatePresence mode="wait" initial={false}>
            <motion.h2
              key={isReschedule ? "r" : step}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.2, delay: 0.08 } }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              className="text-[22px] font-semibold tracking-[-0.02em]"
            >
              {isReschedule ? "Сменить исполнителя или срок" : step === 1 ? "Новая публикация" : "Кому и на какой срок"}
            </motion.h2>
          </AnimatePresence>
          {isReschedule && <p className="truncate text-sm text-muted-foreground">{task.title}</p>}
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} title="Закрыть (Esc)">
          <X className="!size-[18px]" />
        </Button>
      </div>

      {/* popLayout: уходящий шаг сразу выпадает из потока, и модалка меряет высоту уже по новому шагу */}
      <AnimatePresence mode="popLayout" initial={false}>
        {step === 1 ? (
          <motion.div
            key="s1"
            {...stepFade}
            className="scroll-thin flex min-h-0 flex-1 flex-col overflow-y-auto pb-6 pl-7 pr-7 pt-4"
          >
            <div className="mb-5 flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Название</span>
                <motion.input
                  key={titleError}
                  autoFocus
                  animate={titleError ? { x: [0, -6, 6, -3, 0] } : undefined}
                  transition={{ duration: 0.28 }}
                  value={topicTitle}
                  onChange={(e) => setTopicTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && next()}
                  placeholder="Например, Паводки на юге: эвакуация и штаб"
                  className={cn(
                    "h-11 w-full rounded-full border bg-background px-4 text-sm shadow-mxs outline-none transition-shadow placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20",
                    titleError > 0 && topicTitle.trim().length < 3 ? "border-rework" : "border-input",
                  )}
                />
                {titleError > 0 && topicTitle.trim().length < 3 && (
                  <span className="px-4 text-xs text-rework-fg">Назовите публикацию — по названию монтажёр её найдёт</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">Формат</span>
                {PUB_FORMATS.map((f) => (
                  <FilterChip key={f.value} active={f.value === format} onClick={() => setFormat(f.value)}>{f.label}</FilterChip>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Что снять</span>
                <textarea
                  value={description}
                  maxLength={2000}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Хук, о чём ролики, что обязательно показать, чего избегать…"
                  className="w-full resize-none rounded-[20px] border border-input bg-background px-[18px] py-3.5 text-sm leading-relaxed shadow-mxs outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
                />
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="s2"
            {...stepFade}
            className="flex min-h-0 flex-1 flex-col gap-4 pb-5 pl-7 pr-7 pt-3"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium">Направление</span>
              {deptProjects.length > 0 ? (
                // Одна строка выбора направления (08.10.2026): раньше рядом был
                // ещё переключатель над календарём — те же кнопки дважды.
                deptProjects.map((p) => (
                  <FilterChip key={p.id} active={p.id === projectId} onClick={() => changeProject(p.id)}>{p.name}</FilterChip>
                ))
              ) : (
                // Проекта нет — задачу ставить некуда: ролик выходит в паблики
                // проекта, и без него сервер откажет. Говорим, к кому идти.
                <span className="text-[13px] text-rework-fg">
                  В отделе нет направлений — попросите руководителя завести направление и добавить туда вашу команду
                </span>
              )}
            </div>

            {!isReschedule && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium">Аккаунт</span>
                  {projectAccounts.length > 0 ? (
                    <AccountsPick accounts={projectAccounts} value={effectiveAccountIds} onChange={setAccountIds} />
                  ) : (
                    <span className="text-[13px] text-muted-foreground">у направления не указаны аккаунты</span>
                  )}
                </div>
                {hasInstagramAccount && (
                  <label className="ml-[82px] flex cursor-pointer items-center gap-2 text-[13px]">
                    <input type="checkbox" checked={autoPublishInstagram} disabled={afterModeration} onChange={(e) => setAutoPublishInstagram(e.target.checked)} className="size-4 accent-primary disabled:opacity-100" />
                    Автоматически опубликовать в {publicationTarget} после {afterModeration ? "модерации" : "приёмки"}
                  </label>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium">Исполнитель</span>
              {/* Все монтажёры направления — или никого: публикация висит
                  свободной, её берёт первый, кто освободится (08.10.2026). */}
              {deptEditors.map((e) => (
                <FilterChip key={e} active={!unassigned && editor === e} onClick={() => (setEditor(e), setUnassigned(false))}>
                  {PEOPLE[e].name}
                </FilterChip>
              ))}
              {deptEditors.length === 0 && <span className="text-[13px] text-muted-foreground">в направлении нет монтажёров</span>}
              <FilterChip active={unassigned} onClick={() => setUnassigned(true)}>
                Без исполнителя
              </FilterChip>
              {unassigned && <span className="text-[13px] text-muted-foreground">возьмёт любой монтажёр направления</span>}
            </div>

            {period === "week" ? (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">Неделя</span>
                <span className="inline-flex h-8 items-center gap-1.5 rounded-full border bg-background px-3 text-[13px] font-medium">
                  <Flag className="size-3.5 text-muted-foreground" />
                  {dayName(workDay, now)} — {dayName(workDay + 4, now)}, до 20:00
                </span>
                <span className="text-[13px] text-muted-foreground">выберите любой день нужной недели в календаре</span>
              </div>
            ) : (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium">Дедлайн</span>
              {/* Дата и время отдельно, как в планировщике YouTube (Артур, 09.10.2026). */}
              <DueDateTime
                due={due}
                now={now}
                onChange={(v) => {
                  setDue(v);
                  setDay(dayOf(v));
                }}
              />
              {due <= now && <span className="text-[13px] text-rework-fg">срок уже прошёл</span>}
            </div>
            )}
            <div className="no-scrollbar -mx-4 min-h-0 flex-1 overflow-y-auto px-4 pb-2">
              <DayByPeople
                compact
                now={now}
                tasks={tasks}
                busy={busy}
                editors={deptEditors}
                dept={dept}
                onDept={changeDept}
                pick={{ editor: unassigned ? "" : (editor ?? deptEditors[0] ?? EDITORS[0]), day, estimate, size: task ? liveItems(task).length : 1, title: task?.title ?? (topicTitle.trim() || "Новая публикация"), excludeTaskId: task?.id, onPick: pick }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="flex shrink-0 items-center justify-between gap-3 border-t py-4 pl-7 pr-6">
        {step === 1 ? (
          <>
            {isReschedule ? <span /> : (
              <Button variant="ghost" onClick={() => onBatch({ dept, first: { title: topicTitle, format, brief: description } })}>
                <Plus />
                Несколько публикаций
              </Button>
            )}
            <div className="flex gap-2.5">
              <Button variant="outline" onClick={onClose}>
                Отмена
              </Button>
              <Button onClick={next}>
                Далее: исполнитель и срок
                <ArrowRight />
              </Button>
            </div>
          </>
        ) : (
          <>
            <span />
            <div className="flex shrink-0 gap-2.5">
              {!isReschedule && (
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft />
                  Назад
                </Button>
              )}
              <Button onClick={submit} disabled={(!ed && !unassigned) || due <= now}>
                {isReschedule ? <Check /> : <Send />}
                {isReschedule ? "Сохранить" : several ? `Поставить на ${chosen.length} ${plural(chosen.length, ["канал", "канала", "каналов"])}` : unassigned ? "Поставить свободной" : ed ? "Поставить публикацию" : "Нет монтажёра"}
              </Button>
            </div>
          </>
        )}
      </footer>
    </>
  );
}
